import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";

/**
 * Unified site footer used on every page (Landing, Auth, Legal, Layout).
 * 3-zone layout:
 *   - left: brand line
 *   - center: "Join the team" CTA
 *   - right: Support · Terms · Privacy · Cookies
 * Extra bottom padding accommodates the floating "Made with Emergent"
 * badge (178x40px, fixed bottom-right) so it never covers our links.
 *
 * Props:
 *   variant: "app" shows admin/version line for logged-in pages; "site" for public pages.
 */
export default function SiteFooter({ variant = "site" }) {
  // Optional: only read auth context if we're in the app variant.
  // Public-page footers won't have user state, that's fine.
  let user = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    if (variant === "app") user = useAuth().user;
  } catch {
    user = null;
  }

  const linkCls = "text-[10px] tracking-[0.25em] uppercase text-foreground/55 hover:text-primary font-mono whitespace-nowrap";

  return (
    <footer className="border-t border-foreground/30 mt-12 pb-20 sm:pb-16" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-5 md:px-12 py-6">
        {/* row 1: brand / join / link cluster */}
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-4 md:gap-3">
          {/* left */}
          <div className="flex items-center gap-4 md:justify-start justify-center flex-wrap">
            <span className="text-[10px] tracking-[0.25em] uppercase text-foreground/50 font-mono">
              SISYPHEAN · © {new Date().getFullYear()}
            </span>
            {variant === "app" && user?.is_admin && (
              <Link
                to="/admin"
                data-testid="footer-admin"
                className="text-[10px] tracking-[0.25em] uppercase text-primary hover:text-foreground font-mono"
              >
                Admin
              </Link>
            )}
          </div>

          {/* center: Join the team */}
          <div className="flex justify-center">
            <Link to="/join" data-testid="footer-join" className={linkCls}>
              Join the team
            </Link>
          </div>

          {/* right: Support · Terms · Privacy · Cookies */}
          <div className="flex items-center gap-5 md:justify-end justify-center flex-wrap">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent("sisyphean:open-contact"))}
              data-testid="footer-support"
              className={linkCls}
            >
              Support
            </button>
            <Link to="/terms" data-testid="footer-terms" className={linkCls}>
              Terms
            </Link>
            <Link to="/privacy" data-testid="footer-privacy" className={linkCls}>
              Privacy
            </Link>
            <Link to="/cookies" data-testid="footer-cookies" className={linkCls}>
              Cookies
            </Link>
          </div>
        </div>

        {/* row 2 (tiny): consent preferences */}
        <div className="mt-3 flex justify-center md:justify-end">
          <a
            href="#"
            className="termly-display-preferences text-[9px] tracking-[0.22em] uppercase text-foreground/40 hover:text-primary font-mono"
            data-testid="footer-consent-preferences"
          >
            Consent Preferences
          </a>
        </div>
      </div>
    </footer>
  );
}
